'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AuditLog {
  id: string;
  user_id: string | null;
  user_email: string;
  user_role: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  resource_label: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-blue-50 text-blue-700 border-blue-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  LOGIN: 'bg-violet-50 text-violet-700 border-violet-200',
  LOGOUT: 'bg-slate-50 text-slate-600 border-slate-200',
  VIEW: 'bg-amber-50 text-amber-700 border-amber-200',
  EXPORT: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const ACTION_ICONS: Record<string, string> = {
  CREATE: '➕',
  UPDATE: '✏️',
  DELETE: '🗑️',
  LOGIN: '🔑',
  LOGOUT: '🚪',
  VIEW: '👁️',
  EXPORT: '📤',
};

function getActionColor(action: string): string {
  const key = action.toUpperCase();
  return ACTION_COLORS[key] || 'bg-slate-50 text-slate-600 border-slate-200';
}

function getActionIcon(action: string): string {
  const key = action.toUpperCase();
  return ACTION_ICONS[key] || '📋';
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export default function AuditLogsContent() {
  const { isAdmin, userProfile } = useAuth();
  const supabase = createClient();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Unique filter options derived from data
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  const [resourceOptions, setResourceOptions] = useState<string[]>([]);

  const canAccess = isAdmin;

  const fetchLogs = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (dateFrom) query = query.gte('created_at', dateFrom);
      if (dateTo) query = query.lte('created_at', dateTo + 'T23:59:59');

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const rows = (data || []) as AuditLog[];
      setLogs(rows);

      // Build unique filter options
      const actions = [...new Set(rows.map(r => r.action).filter(Boolean))].sort();
      const resources = [...new Set(rows.map(r => r.resource_type).filter(Boolean))].sort();
      setActionOptions(actions);
      setResourceOptions(resources);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [canAccess, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [search, filterAction, filterResource, filterUser]);

  // Client-side filtering
  const filtered = logs.filter(log => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      log.user_email?.toLowerCase().includes(q) ||
      log.action?.toLowerCase().includes(q) ||
      log.resource_type?.toLowerCase().includes(q) ||
      log.resource_label?.toLowerCase().includes(q) ||
      log.resource_id?.toLowerCase().includes(q);

    const matchAction = !filterAction || log.action === filterAction;
    const matchResource = !filterResource || log.resource_type === filterResource;
    const matchUser = !filterUser || log.user_email?.toLowerCase().includes(filterUser.toLowerCase());

    return matchSearch && matchAction && matchResource && matchUser;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const clearFilters = () => {
    setSearch('');
    setFilterAction('');
    setFilterResource('');
    setFilterUser('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || filterAction || filterResource || filterUser || dateFrom || dateTo;

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-lg font-600 text-slate-800 mb-1">Accès restreint</h3>
          <p className="text-sm text-slate-500">
            Seuls les Administrateurs et Brokers Senior peuvent consulter les logs d'audit.
          </p>
          <p className="text-xs text-slate-400 mt-1">Votre rôle actuel : <span className="font-500">{userProfile?.role || 'Inconnu'}</span></p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Audit Logs</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Traçabilité complète des actions utilisateurs sur la plateforme
          </p>
        </div>
        <button
          onClick={fetchLogs}
          className="flex items-center gap-2 px-4 py-2 text-sm font-500 text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total logs', value: logs.length, icon: '📋', color: 'bg-slate-50 border-slate-200' },
          { label: 'Résultats filtrés', value: filtered.length, icon: '🔍', color: 'bg-blue-50 border-blue-200' },
          { label: 'Actions uniques', value: actionOptions.length, icon: '⚡', color: 'bg-violet-50 border-violet-200' },
          { label: 'Ressources', value: resourceOptions.length, icon: '🏢', color: 'bg-emerald-50 border-emerald-200' },
        ].map(stat => (
          <div key={stat.label} className={`flex items-center gap-3 p-3 rounded-xl border ${stat.color}`}>
            <span className="text-xl">{stat.icon}</span>
            <div>
              <p className="text-lg font-700 text-slate-800">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
        {/* Search */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher par utilisateur, action, ressource..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-50"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-2">
          <select
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-700"
          >
            <option value="">Toutes les actions</option>
            {actionOptions.map(a => (
              <option key={a} value={a}>{getActionIcon(a)} {a}</option>
            ))}
          </select>

          <select
            value={filterResource}
            onChange={e => setFilterResource(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-700"
          >
            <option value="">Toutes les ressources</option>
            {resourceOptions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Filtrer par utilisateur..."
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-700 min-w-[180px]"
          />

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-700"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-slate-700"
            />
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-sm text-red-600 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">Chargement des logs...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="text-3xl">⚠️</span>
            <p className="text-sm text-red-600 font-500">{error}</p>
            <button onClick={fetchLogs} className="text-sm text-primary-600 hover:underline">Réessayer</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="text-4xl">📋</span>
            <p className="text-sm text-slate-500">Aucun log trouvé avec ces critères</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-primary-600 hover:underline">Effacer les filtres</button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">Timestamp</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">Utilisateur</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">Action</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">Ressource</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">Détails</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-slate-500 uppercase tracking-wider whitespace-nowrap">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginated.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      {/* Timestamp */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-slate-500 font-mono">{formatDate(log.created_at)}</span>
                      </td>

                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-slate-800 font-500 text-xs truncate max-w-[160px]">{log.user_email || '—'}</span>
                          {log.user_role && (
                            <span className="text-[10px] text-slate-400">{log.user_role}</span>
                          )}
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-500 border ${getActionColor(log.action)}`}>
                          <span>{getActionIcon(log.action)}</span>
                          {log.action}
                        </span>
                      </td>

                      {/* Resource */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-slate-700 font-500 text-xs">{log.resource_type}</span>
                          {log.resource_label && (
                            <span className="text-[10px] text-slate-400 truncate max-w-[140px]">{log.resource_label}</span>
                          )}
                          {log.resource_id && !log.resource_label && (
                            <span className="text-[10px] text-slate-400 font-mono truncate max-w-[140px]">{log.resource_id}</span>
                          )}
                        </div>
                      </td>

                      {/* Details */}
                      <td className="px-4 py-3 max-w-[200px]">
                        {log.details ? (
                          <span className="text-xs text-slate-500 truncate block" title={JSON.stringify(log.details)}>
                            {Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>

                      {/* IP */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-slate-400 font-mono">{log.ip_address || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <span className="text-xs text-slate-500">
                  {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} sur {filtered.length} résultats
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p = i + 1;
                    if (totalPages > 5) {
                      if (page <= 3) p = i + 1;
                      else if (page >= totalPages - 2) p = totalPages - 4 + i;
                      else p = page - 2 + i;
                    }
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 rounded-md text-xs font-500 transition-colors ${page === p ? 'bg-primary-600 text-white' : 'border border-slate-200 text-slate-600 hover:bg-white'}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
